<script lang="ts">
  /**
   * Everything for one layer: its visualization, the visualization's controls, and its look:
   * colours and effects, each either shared with all layers or this layer's own.
   */
  import type { AppController } from '../../app/controller.svelte';
  import { VIEW_PARAMS, viewValue, type Layer } from '../../shared/scene';
  import EffectsEditor from './EffectsEditor.svelte';
  import PalettePicker from './PalettePicker.svelte';
  import ParamControl from './ParamControl.svelte';
  import VizPicker from './VizPicker.svelte';
  import Icon from './Icon.svelte';

  let { ctl, layer, index }: { ctl: AppController; layer: Layer; index: number } = $props();
  const def = $derived(ctl.vizDef(layer.vizId));
  const multi = $derived(ctl.scene.layers.length > 1);
  let picking = $state(true);
  const ownFx = $derived(Object.keys(layer.postfx ?? {}).length);
</script>

<div class="block" data-tour="viz-browser">
  <div class="head">
    <h3>Visualization</h3>
    <button
      class="current"
      data-testid="viz-change"
      aria-expanded={picking}
      onclick={() => (picking = !picking)}
      >{def?.name ?? layer.vizId}
      <span class="muted"><Icon name={picking ? 'chevron-up' : 'chevron-down'} /></span></button
    >
  </div>
  {#if picking}
    <VizPicker
      {ctl}
      current={layer.vizId}
      onpick={(id) => ctl.dispatch({ type: 'layer.set', layerId: layer.id, patch: { vizId: id } })}
    />
  {/if}
</div>

<div class="block" data-tour="layer-controls">
  <div class="head">
    <h3>Controls</h3>
    <button
      class="link"
      onclick={() => ctl.dispatch({ type: 'layer.resetParams', layerId: layer.id })}>Reset</button
    >
  </div>
  {#if multi}
    <ParamControl
      {ctl}
      def={{ id: 'opacity', label: 'Opacity', type: 'float', min: 0, max: 1, default: 1 }}
      value={layer.opacity}
      target={`layer/${layer.id}/opacity`}
      midiPath={`layer#${index}/opacity`}
      onchange={(v) =>
        ctl.dispatch(
          { type: 'layer.set', layerId: layer.id, patch: { opacity: v } },
          `${layer.id}/opacity`,
        )}
    />
  {/if}
  {#each def?.params ?? [] as p (p.id)}
    <ParamControl
      {ctl}
      def={p}
      value={layer.params[p.id] ?? p.default}
      target={`layer/${layer.id}/${p.id}`}
      midiPath={`layer#${index}/${p.id}`}
      onchange={(v) =>
        ctl.dispatch(
          { type: 'layer.param', layerId: layer.id, paramId: p.id, value: v },
          `${layer.id}/${p.id}`,
        )}
    />
  {/each}
  {#if def?.credit}<p class="hint">{def.credit}</p>{/if}
</div>

<details class="block look" data-testid="layer-view">
  <summary>
    <h3>View</h3>
    <span class="chip" class:accent={!!layer.view}>{layer.view ? 'changed' : 'as drawn'}</span>
  </summary>
  <p class="hint">Move, zoom and sharpen this layer's picture.</p>
  {#each VIEW_PARAMS as p (p.id)}
    <ParamControl
      {ctl}
      def={p}
      value={viewValue(layer, p.id)}
      target={`layer/${layer.id}/view/${p.id}`}
      midiPath={`layer#${index}/view/${p.id}`}
      onchange={(v) =>
        ctl.dispatch(
          { type: 'layer.view', layerId: layer.id, key: p.id, value: v },
          `${layer.id}/view/${p.id}`,
        )}
    />
  {/each}
  {#if layer.view}
    <button
      class="link"
      data-testid="layer-view-reset"
      onclick={() => ctl.dispatch({ type: 'layer.resetView', layerId: layer.id })}
      >Reset view</button
    >
  {/if}
</details>

<div data-tour="layer-look">
  <details class="block look" data-testid="layer-colours">
    <summary>
      <h3>Colours</h3>
      <span class="chip" class:accent={!!layer.palette}
        >{layer.palette ? 'this layer' : 'overall'}</span
      >
    </summary>
    <div class="seg" role="group" aria-label="Palette for this layer">
      <button
        class:active={!layer.palette}
        aria-pressed={!layer.palette}
        onclick={() => layer.palette && ctl.setLayerOwnPalette(layer.id, false)}
        >Overall palette</button
      >
      <button
        class:active={!!layer.palette}
        aria-pressed={!!layer.palette}
        data-testid="layer-own-palette"
        onclick={() => !layer.palette && ctl.setLayerOwnPalette(layer.id, true)}
        >This layer’s own</button
      >
    </div>
    {#if layer.palette}
      <PalettePicker
        {ctl}
        current={layer.palette}
        testid="layer-palette"
        onpick={(palette) => ctl.dispatch({ type: 'layer.palette', layerId: layer.id, palette })}
      />
      <ParamControl
        {ctl}
        def={{ id: 'shift', label: 'Hue shift', type: 'float', min: 0, max: 1, default: 0 }}
        value={layer.paletteShift ?? 0}
        target={`layer/${layer.id}/palette/shift`}
        midiPath={`layer#${index}/palette/shift`}
        onchange={(v) =>
          ctl.dispatch(
            { type: 'layer.paletteShift', layerId: layer.id, value: v },
            `${layer.id}/palette-shift`,
          )}
      />
    {:else}
      <p class="hint">
        Uses the overall palette (below the layers). Switch to give this layer its own.
      </p>
    {/if}
  </details>

  <details class="block look" data-testid="layer-effects">
    <summary>
      <h3>Effects</h3>
      {#if ownFx}<span class="chip accent">{ownFx} on this layer</span>{/if}
    </summary>
    <p class="hint">
      Each effect applies to <b>all layers</b> (the finished picture) or to <b>this layer only</b>.
    </p>
    <EffectsEditor {ctl} {layer} layerIndex={index} />
  </details>
</div>

<style>
  .block {
    padding: 6px 0 4px;
  }
  .head,
  summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
  }
  summary {
    cursor: pointer;
    justify-content: flex-start;
  }
  h3 {
    margin: 0;
    font-size: 11.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--muted);
  }
  .look {
    border-top: 1px solid var(--border);
  }
  .current {
    font-size: 12.5px;
    max-width: 230px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .seg {
    display: flex;
    gap: 4px;
    margin: 4px 0 8px;
  }
  .seg button {
    flex: 1;
    font-size: 11.5px;
    padding: 3px 4px;
  }
</style>
